import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/utils/supabaseClient.js';
import { useAuth } from '@/context/AuthContext.jsx';

const isDevEnvironment = Boolean(import.meta.env?.DEV);

const buildState = ({
  session = null,
  user = null,
  error = null,
  isChecking = false,
  refreshed = false,
}) => ({
  session,
  user,
  error,
  isChecking,
  refreshed,
});

/**
 * Ensure a Supabase session is available, optionally attempting a refresh.
 *
 * Returns `{ session, user, isChecking, error, hasSession, refreshed }`.
 */
export const useSessionGuard = ({ autoRefresh = true } = {}) => {
  const { session, user, isLoading, refreshUser, permissions } = useAuth();
  const [state, setState] = useState(
    buildState({
      session,
      user,
      isChecking: isLoading,
    }),
  );

  useEffect(() => {
    let isActive = true;

    const ensureSession = async () => {
      if (isLoading) {
        setState((prev) =>
          buildState({
            ...prev,
            session,
            user,
            isChecking: true,
          }),
        );
        return;
      }

      if (session?.access_token) {
        setState(
          buildState({
            session,
            user,
            isChecking: false,
            refreshed: false,
          }),
        );
        return;
      }

      if (!autoRefresh) {
        setState(
          buildState({
            session: null,
            user: null,
            error: null,
            isChecking: false,
            refreshed: false,
          }),
        );
        return;
      }

      setState((prev) =>
        buildState({
          ...prev,
          isChecking: true,
        }),
      );

      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!isActive) {
          return;
        }

        if (error) {
          setState(
            buildState({
              session: null,
              user: null,
              error,
              isChecking: false,
              refreshed: false,
            }),
          );
          return;
        }

        const refreshedSession = data?.session || null;
        if (refreshedSession?.access_token) {
          await refreshUser();
          if (!isActive) {
            return;
          }
          setState(
            buildState({
              session: refreshedSession,
              user: refreshedSession.user || user || null,
              error: null,
              isChecking: false,
              refreshed: true,
            }),
          );
        } else {
          setState(
            buildState({
              session: null,
              user: null,
              error: null,
              isChecking: false,
              refreshed: false,
            }),
          );
        }
      } catch (refreshError) {
        if (!isActive) {
          return;
        }
        setState(
          buildState({
            session: null,
            user: null,
            error: refreshError,
            isChecking: false,
            refreshed: false,
          }),
        );
      }
    };

    ensureSession();

    return () => {
      isActive = false;
    };
  }, [autoRefresh, isLoading, refreshUser, session, user]);

  const computed = useMemo(() => {
    const resolvedUser = state.user || (isDevEnvironment ? user || null : null);
    const hasSession =
      Boolean(state.session?.access_token) ||
      (isDevEnvironment && Boolean(resolvedUser?.id));

    return {
      ...state,
      user: resolvedUser,
      hasSession,
      permissions: Array.isArray(permissions) ? permissions : [],
    };
  }, [state, user, permissions]);

  return computed;
};

export default useSessionGuard;

